import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle } from "lucide-react";
import { PERMISSIONS_BY_CATEGORY } from "@/types/roles";

interface PermissionsSelectorProps {
  selectedPermissions: string[];
  onPermissionsChange: (permissions: string[]) => void;
  error?: string;
}

export function PermissionsSelector({
  selectedPermissions,
  onPermissionsChange,
  error,
}: PermissionsSelectorProps) {
  const permissionsByCategory = PERMISSIONS_BY_CATEGORY;

  const togglePermission = (permissionId: string) => {
    if (selectedPermissions.includes(permissionId)) {
      onPermissionsChange(
        selectedPermissions.filter((p) => p !== permissionId),
      );
    } else {
      onPermissionsChange([...selectedPermissions, permissionId]);
    }
  };

  const toggleCategory = (category: string) => {
    const categoryPermissions =
      permissionsByCategory[category]?.map((p) => p.id) || [];

    const allSelected = categoryPermissions.every((p) =>
      selectedPermissions.includes(p),
    );

    if (allSelected) {
      onPermissionsChange(
        selectedPermissions.filter((p) => !categoryPermissions.includes(p)),
      );
    } else {
      const newPermissions = [...selectedPermissions];
      categoryPermissions.forEach((p) => {
        if (!newPermissions.includes(p)) {
          newPermissions.push(p);
        }
      });
      onPermissionsChange(newPermissions);
    }
  };

  const isCategorySelected = (category: string) => {
    const categoryPermissions =
      permissionsByCategory[category]?.map((p) => p.id) || [];
    return (
      categoryPermissions.length > 0 &&
      categoryPermissions.every((p) => selectedPermissions.includes(p))
    );
  };

  const isCategoryPartiallySelected = (category: string) => {
    const categoryPermissions =
      permissionsByCategory[category]?.map((p) => p.id) || [];
    const selectedCount = categoryPermissions.filter((p) =>
      selectedPermissions.includes(p),
    ).length;

    return selectedCount > 0 && selectedCount < categoryPermissions.length;
  };

  const totalPermissions = Object.values(permissionsByCategory).flat().length;

  return (
    <div>
      <Label className="mb-3 block">Permisos *</Label>

      {error && (
        <p className="text-red-500 text-sm mb-3 flex items-center">
          <AlertCircle className="h-3 w-3 mr-1" />
          {error}
        </p>
      )}

      <div className="space-y-4 border rounded-lg p-4 max-h-96 overflow-y-auto bg-slate-50">
        {Object.entries(permissionsByCategory).map(
          ([category, permissions]) => (
            <div key={category} className="space-y-2">
              {/* CATEGORY */}
              <div className="flex items-center space-x-2 border-b pb-2">
                <Checkbox
                  id={`category-${category}`}
                  checked={isCategorySelected(category)}
                  onCheckedChange={() => toggleCategory(category)}
                  className={
                    isCategoryPartiallySelected(category)
                      ? "data-[state=checked]:bg-blue-400"
                      : ""
                  }
                />
                <Label
                  htmlFor={`category-${category}`}
                  className="font-semibold text-sm text-slate-700 cursor-pointer"
                >
                  {category}
                  <span className="ml-2 text-xs text-slate-500 font-normal">
                    (
                    {
                      permissions.filter((p) =>
                        selectedPermissions.includes(p.id),
                      ).length
                    }
                    /{permissions.length})
                  </span>
                </Label>
              </div>

              {/* PERMISSIONS */}
              <div className="grid grid-cols-2 gap-2 pl-6">
                {permissions.map((permission) => (
                  <div
                    key={permission.id}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={permission.id}
                      checked={selectedPermissions.includes(permission.id)}
                      onCheckedChange={() => togglePermission(permission.id)}
                    />
                    <Label
                      htmlFor={permission.id}
                      className="text-sm cursor-pointer text-slate-600"
                    >
                      {permission.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          ),
        )}
      </div>

      <p className="text-xs text-slate-500 mt-2">
        {selectedPermissions.length} permisos seleccionados de{" "}
        {totalPermissions}
      </p>
    </div>
  );
}
